import { useCallback, useEffect, useRef } from 'react'
import useSWR from 'swr'
import {
  SocialSyncSchedule,
  SocialUniverseItem,
  SocialUniversesResponse,
} from '@digital-twins/shared-types'
import { isUniverseInProgress } from './socialTrackerStatus'
import { useSocialListeningApi } from './SocialListeningApiContext'
import { refreshSocialPostsUsage } from './useSocialPostsUsage'
import { useAuth } from '@/lib/config/auth-provider'
import { onSocialTrackerUpdated } from '@/lib/services/notifications-socket'

export const SOCIAL_UNIVERSES_SWR_KEY = 'social-listening/universes'

/** While any Topic is collecting or sorting, refetch on this interval so its card follows it. */
export const SOCIAL_UNIVERSES_POLL_MS = 3000

/**
 * True while a Topic's channels are collecting or a run is in its media,
 * judge or digest phase. `postsPending` alone does not count: posts a closed
 * run left unjudged (a Jev outage) wait for a later judge, possibly hours
 * away, and polling every few seconds meanwhile would never stop.
 */
export function isSocialUniverseBusy(
  universe: Pick<SocialUniverseItem, 'channels'>,
): boolean {
  return isUniverseInProgress(universe.channels)
}

export function getSocialUniversesRefreshInterval(
  latest: SocialUniversesResponse | undefined,
): number {
  const hasBusy = latest?.universes.some((universe) => isSocialUniverseBusy(universe))
  return hasBusy ? SOCIAL_UNIVERSES_POLL_MS : 0
}

interface UseSocialUniversesResult {
  isEnabled: boolean
  canManage: boolean
  schedule: SocialSyncSchedule
  universes: Array<SocialUniverseItem>
  isLoading: boolean
  hasError: boolean
  /** Refetches the Topics and the posts meter. */
  refresh: () => Promise<SocialUniversesResponse | undefined>
  /** Swaps one Topic in the cached list, or adds it first (mutation responses return the item). */
  replaceUniverse: (universe: SocialUniverseItem) => void
  removeUniverse: (token: string) => void
}

/**
 * Topic list for the Library's Social listening tab. Polls while a Topic is
 * busy and refetches whenever the notifications socket says a channel changed.
 * The posts meter is refreshed when an update finishes, since every fetched
 * post counts. A failed request reads as "not enabled" so the Library looks as
 * it does without the feature. With the org flag
 * (`user.featureFlags.socialListening`) off no request is made.
 */
export function useSocialUniverses(shouldLoad: boolean): UseSocialUniversesResult {
  const socialListeningApi = useSocialListeningApi()
  const { user } = useAuth()
  const isOrgFlagOn = user?.featureFlags?.socialListening === true
  const { data, error, isLoading, mutate } = useSWR<SocialUniversesResponse>(
    shouldLoad && isOrgFlagOn ? SOCIAL_UNIVERSES_SWR_KEY : null,
    () => socialListeningApi.listUniverses(),
    {
      revalidateOnFocus: false,
      refreshInterval: getSocialUniversesRefreshInterval,
    },
  )
  const isEnabled = isOrgFlagOn && Boolean(data?.enabled) && !error
  const isBusy = Boolean(data?.universes.some((universe) => isSocialUniverseBusy(universe)))
  const wasBusyRef = useRef(isBusy)

  useEffect(() => {
    if (!shouldLoad || !isEnabled) {
      return
    }
    return onSocialTrackerUpdated(() => {
      void mutate()
      void refreshSocialPostsUsage()
    })
  }, [shouldLoad, isEnabled, mutate])

  useEffect(() => {
    if (wasBusyRef.current && !isBusy) {
      void refreshSocialPostsUsage()
    }
    wasBusyRef.current = isBusy
  }, [isBusy])

  const refresh = useCallback(async () => {
    const [latest] = await Promise.all([mutate(), refreshSocialPostsUsage()])
    return latest
  }, [mutate])

  const replaceUniverse = useCallback(
    (universe: SocialUniverseItem) => {
      void mutate(
        (current) => {
          if (!current) {
            return current
          }
          const exists = current.universes.some((item) => item.token === universe.token)
          return {
            ...current,
            universes: exists
              ? current.universes.map((item) => (item.token === universe.token ? universe : item))
              : [universe, ...current.universes],
          }
        },
        { revalidate: false },
      )
    },
    [mutate],
  )

  const removeUniverse = useCallback(
    (token: string) => {
      void mutate(
        (current) =>
          current
            ? { ...current, universes: current.universes.filter((item) => item.token !== token) }
            : current,
        { revalidate: false },
      )
    },
    [mutate],
  )

  return {
    isEnabled,
    canManage: Boolean(data?.canManage),
    schedule: data?.schedule ?? 'daily',
    universes: data?.universes ?? [],
    isLoading: shouldLoad && isOrgFlagOn && isLoading,
    hasError: Boolean(error),
    refresh,
    replaceUniverse,
    removeUniverse,
  }
}
